import React, { forwardRef, useEffect, useRef } from "react";
import Input, { InputProps } from "../Input/Input";
import { Loader } from "../Loader/Loader";
import {
  StyledOptionsList,
  StyledInputOptionsContainer,
  StyledSelectContainer,
  StyledOptionContainer,
  StyledOptionItemSelect,
  StyledOptionsScrollArea,
} from "./InputWithOptions.styled";
import { StyledLoaderEmptyContent } from "../Select/Select.style";
import { FontWeightEnum, IconSizeEnum } from "../../../types/AppTypes";
import { BodyTyp } from "../../Typography/Typography";
import { theme } from "../../../constants/StyledComponents";
import { useMergedRefs } from "../../../hooks/useMergedRefs.hook";
import { IconManager } from "../IconManager/IconManager";

type InputOption<T> = {
  value: T;
  label: string | React.ReactNode;
  disabled?: boolean;
};

export interface InputWithOptionsProps<T> extends InputProps {
  options?: InputOption<T>[];
  canLoadMore?: boolean;
  fetchMore?: () => void;
  onSelectOption?: (x: T) => void;
  fetchingMore?: boolean;
  loading?: boolean;
  optionsEmptyMessage?: string;
}

function InputWithOptionsInner<T extends string | number>(
  {
    options,
    canLoadMore,
    fetchMore,
    onSelectOption,
    fetchingMore,
    loading,
    optionsEmptyMessage,
    ...props
  }: InputWithOptionsProps<T>,
  forwardedRef: React.ForwardedRef<HTMLDivElement>,
) {
  const [isOpen, setIsOpen] = React.useState(false);

  const useOutsideAlerter = (ref: React.RefObject<HTMLDivElement>) => {
    useEffect(() => {
      function handleClickOutside(event: MouseEvent) {
        if (
          ref.current &&
          !ref.current.contains(event.target as Node) &&
          setIsOpen
        ) {
          setIsOpen(false);
        }
      }

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  };

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const mergedRef = useMergedRefs(wrapperRef, forwardedRef);

  useOutsideAlerter(wrapperRef);

  const scrollOptionsRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    if (
      !scrollOptionsRef.current ||
      !fetchMore ||
      fetchingMore ||
      loading ||
      !canLoadMore
    ) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = scrollOptionsRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      if (!fetchingMore && canLoadMore) {
        fetchMore();
      }
    }
  };

  const showOptions = !loading && isOpen;

  const renderOptions = () => {
    if (optionsEmptyMessage && options && options.length === 0) {
      return (
        <StyledOptionsList>
          <StyledOptionContainer>
            <StyledOptionItemSelect disabled={true}>
              <BodyTyp variant="2" fontWeight={FontWeightEnum.MEDIUM}>
                {optionsEmptyMessage}
              </BodyTyp>
            </StyledOptionItemSelect>
          </StyledOptionContainer>
        </StyledOptionsList>
      );
    }

    if (options && options.length > 0) {
      return (
        <StyledOptionsList onScroll={handleScroll}>
          <StyledOptionsScrollArea
            ref={scrollOptionsRef}
            onScroll={handleScroll}
          >
            {options?.map((option) => (
              <StyledOptionContainer key={option.value}>
                <StyledOptionItemSelect
                  disabled={option.disabled}
                  $selected={option.value === props.value}
                  onClick={() => {
                    if (option.disabled) {
                      return;
                    }

                    if (onSelectOption) {
                      onSelectOption(option.value);
                    }
                    setIsOpen(false);
                  }}
                >
                  {typeof option.label === "string" ? (
                    <BodyTyp
                      variant="2"
                      fontWeight={FontWeightEnum.MEDIUM}
                      color={
                        option.value === props.value
                          ? theme.colors.white
                          : theme.colors.black
                      }
                    >
                      {option.label}
                    </BodyTyp>
                  ) : (
                    option.label
                  )}
                </StyledOptionItemSelect>
              </StyledOptionContainer>
            ))}
            {fetchingMore && (
              <Loader loading={fetchingMore} size={20}>
                <StyledLoaderEmptyContent />
              </Loader>
            )}
          </StyledOptionsScrollArea>
        </StyledOptionsList>
      );
    }
  };

  return (
    <StyledSelectContainer ref={mergedRef}>
      <StyledInputOptionsContainer>
        <Input
          {...props}
          {...(props.endIcon && {
            endIcon: loading ? (
              <Loader loading={loading} size={18} />
            ) : (
              <IconManager
                size={IconSizeEnum.SMALL}
                color={
                  props.disabled ? theme.colors.grey_4 : theme.colors.black
                }
                {...(!loading &&
                  !props.disabled && {
                    onClick: () => setIsOpen(!isOpen),
                  })}
              >
                {props.endIcon}
              </IconManager>
            ),
          })}
          onClick={() => setIsOpen(true)}
        />
      </StyledInputOptionsContainer>
      {showOptions ? renderOptions() : null}
    </StyledSelectContainer>
  );
}

export const InputWithOptions = forwardRef(InputWithOptionsInner) as <
  T extends string | number,
>(
  props: InputWithOptionsProps<T> & React.RefAttributes<HTMLDivElement>,
) => ReturnType<typeof InputWithOptionsInner>;
